import { ref } from '@vue/composition-api'
import { findIndex } from 'lodash'
import CustomerApi from '@/api/waiter/Customer'
import { handleApiError } from '@/utils/errorHandler'

export default function useCustomers(vm) {
  const customers = ref([])
  const selectedCustomer = ref(null)
  const newCustomerDialog = ref(false)
  const editDialog = ref(false)
  const searchQuery = ref('')
  const isSearching = ref(false)
  const pagination = ref({
    page: 1,
    perPage: 50,
    totalCount: 0,
    totalPages: 0,
  })

  const getCustomers = async ({ page = 1, perPage = 50, q = '' } = {}) => {
    try {
      const params = { page, perPage }
      if (q) params.q = q

      const res = await CustomerApi.getCustomers(params)

      if (res.data && res.data.data) {
        customers.value = res.data.data || []
        pagination.value = res.data.pagination || {
          page: 1,
          perPage: 50,
          totalCount: 0,
          totalPages: 0,
        }
      }
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })
    }
  }

  // API検索（サーバーサイド検索）
  const searchCustomers = async (query, { page = 1, perPage = 50 } = {}) => {
    isSearching.value = true
    try {
      await getCustomers({ page, perPage, q: query?.trim() || '' })
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })
    } finally {
      isSearching.value = false
    }
  }

  const getCustomer = async id => {
    try {
      const res = await CustomerApi.getCustomer(id)

      if (res.data && res.data.data) {
        return res.data.data
      }

      return null
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return null
    }
  }

  const createCustomer = async ({
    name,
    birthday,
    receiptAddressee,
    callback,
  }) => {
    try {
      const res = await CustomerApi.createCustomer({
        name,
        birthday,
        receiptAddressee,
      })

      if (res.data && res.data.data) {
        customers.value = [res.data.data, ...customers.value]
        vm.$toast.success('顧客を新規作成しました')

        return true
      }

      return false
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      if (callback) callback()
    }
  }

  const updateCustomer = async ({
    id,
    name,
    birthday,
    receiptAddressee,
    customer_custom_field_values_attributes,
    callback,
  }) => {
    try {
      const params = {}
      if (name !== undefined) params.name = name
      if (birthday !== undefined) params.birthday = birthday
      if (receiptAddressee !== undefined) params.receiptAddressee = receiptAddressee
      if (customer_custom_field_values_attributes !== undefined) {
        params.customerCustomFieldValuesAttributes = customer_custom_field_values_attributes
      }

      const res = await CustomerApi.updateCustomer(id, params)

      if (res.data && res.data.data) {
        const updatedCustomer = res.data.data
        const customerIdx = findIndex(customers.value, customer => Number(customer.id) === Number(updatedCustomer.id))
        if (customerIdx >= 0) {
          customers.value.splice(customerIdx, 1, updatedCustomer)
        }
        vm.$toast.success('顧客を更新しました')

        return updatedCustomer
      }

      return false
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      if (callback) callback()
    }
  }

  const deleteCustomer = async ({ id, callback }) => {
    try {
      await CustomerApi.deleteCustomer(id)

      const customerIdx = findIndex(customers.value, customer => Number(customer.id) === Number(id))
      if (customerIdx >= 0) {
        customers.value.splice(customerIdx, 1)
      }
      vm.$toast.success('顧客を削除しました')

      return true
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      if (callback) callback()
    }
  }

  const getVisitHistory = async (id, { page = 1, perPage = 10 } = {}) => {
    try {
      const res = await CustomerApi.getVisitHistory(id, { page, perPage })

      return {
        visitHistory: res.data?.visitHistory || [],
        pagination: res.data?.pagination || {
          page: 1, perPage: 10, totalCount: 0, totalPages: 0,
        },
      }
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return {
        visitHistory: [],
        pagination: {
          page: 1, perPage: 10, totalCount: 0, totalPages: 0,
        },
      }
    }
  }

  const getPurchaseHistory = async (id, { page = 1, perPage = 10 } = {}) => {
    try {
      const res = await CustomerApi.getPurchaseHistory(id, { page, perPage })

      return res.data || {}
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return {}
    }
  }

  const openNewCustomerDialog = () => {
    newCustomerDialog.value = true
  }

  const openEditDialog = customer => {
    selectedCustomer.value = customer
    editDialog.value = true
  }

  return {
    customers,
    selectedCustomer,
    newCustomerDialog,
    editDialog,
    searchQuery,
    isSearching,
    pagination,
    getCustomers,
    getCustomer,
    searchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    getVisitHistory,
    getPurchaseHistory,
    openNewCustomerDialog,
    openEditDialog,
  }
}
